import { useCallback, useMemo, useState } from "react";
import { Clapperboard, RotateCcw, Wand2 } from "lucide-react";
import { HybridFormField } from "@/components/HybridFormField";
import { PromptResult } from "@/components/PromptResult";
import { getMasterPromptTemplate } from "@/data/masterPromptTemplates";
import { buildShortVideoPrompt, createEmptyForm } from "@/lib/masterPromptBuilder";
import { usePromptLibrary } from "@/hooks/usePromptLibrary";

const template = getMasterPromptTemplate("lyhytvideo-kasikirjoitus");

export default function ShortVideoPromptPage() {
  const emptyForm = useMemo(() => createEmptyForm(template), []);
  const [form, setForm] = useState(emptyForm);
  const [prompt, setPrompt] = useState("");
  const { savePrompt } = usePromptLibrary();

  const update = useCallback((key, nextValue) => setForm((current) => ({ ...current, [key]: nextValue })), []);
  const updateOther = useCallback((key, nextValue) => setForm((current) => ({ ...current, [`${key}__other`]: nextValue })), []);
  const selectedAiTool = form.aiTyokalu === "Muu, mikä?" ? form.aiTyokalu__other : form.aiTyokalu;

  const fillExample = useCallback(() => {
    setForm({ ...emptyForm, ...template.esimerkkitaytto });
    setPrompt("");
  }, [emptyForm]);

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setPrompt("");
  }, [emptyForm]);

  const generatePrompt = useCallback(() => setPrompt(buildShortVideoPrompt(form, template)), [form]);
  const saveShortVideoPrompt = useCallback(({ title, prompt: finalPrompt }) => savePrompt({ title, type: "Lyhytvideot", prompt: finalPrompt, aiTool: selectedAiTool }), [savePrompt, selectedAiTool]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="short-video-page">
      <div className="mb-8 max-w-3xl" data-testid="short-video-heading-block">
        <p className="text-sm font-extrabold uppercase text-[#D97757]" data-testid="short-video-kicker">Valmis pohja</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-[#2C2F2E] sm:text-5xl" data-testid="short-video-title">Lyhytvideon käsikirjoitus</h1>
        <p className="mt-4 text-base leading-7 text-[#5C6360]" data-testid="short-video-description">Täytä videon tärkeimmät tiedot ja Studio muodostaa huolellisen promptin, jolla saat luonnollisen, kuvauskelpoisen käsikirjoituksen Reelsiin, TikTokiin tai Shortsiin.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.85fr)]" data-testid="short-video-workspace">
        <section className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7" data-testid="short-video-form-section">
          <div className="mb-6 rounded-[1.25rem] bg-[#F5F3EC] p-5" data-testid="short-video-template-summary">
            <div className="mb-3 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-2xl bg-[#4A6753] text-white" data-testid="short-video-summary-icon"><Clapperboard className="h-5 w-5" aria-hidden="true" /></span>
              <div>
                <h2 className="font-heading text-xl font-bold text-[#2C2F2E]" data-testid="short-video-summary-title">{template.nimi}</h2>
                <p className="text-xs font-bold uppercase text-[#D97757]" data-testid="short-video-summary-category">{template.kategoria}</p>
              </div>
            </div>
            <p className="text-sm leading-7 text-[#5C6360]" data-testid="short-video-summary-description">{template.kayttotarkoitus}</p>
            <div className="mt-5" data-testid="short-video-summary-fields">
              <p className="text-sm font-bold text-[#2C2F2E]" data-testid="short-video-summary-fields-title">Täytettävät tiedot</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="short-video-summary-field-list">
                {template.lomakekentat.map((field) => (
                  <span key={field.id} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#4A6753] ring-1 ring-[#E8E5D9]" data-testid={`short-video-summary-field-${field.id}`}>
                    {field.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-6 flex flex-wrap gap-2" data-testid="short-video-helper-actions">
            <button type="button" onClick={fillExample} className="inline-flex items-center gap-2 rounded-full bg-[#D97757] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" data-testid="short-video-fill-example-button">
              <Wand2 className="h-4 w-4" aria-hidden="true" /> Täytä esimerkillä
            </button>
            <button type="button" onClick={resetForm} className="inline-flex items-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5" data-testid="short-video-reset-button">
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Tyhjennä
            </button>
          </div>

          <div className="mb-4" data-testid="short-video-form-heading">
            <h2 className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="short-video-form-title">Täytä videon tiedot</h2>
            <p className="mt-1 text-sm leading-6 text-[#5C6360]" data-testid="short-video-form-description">Voit täyttää vain tärkeimmät kohdat tai käyttää esimerkkiä pohjana.</p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="short-video-form-grid">
            {template.lomakekentat.map((field) => (
              <div key={field.id} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                <HybridFormField
                  field={field}
                  value={form[field.id]}
                  onChange={(nextValue) => update(field.id, nextValue)}
                  otherValue={form[`${field.id}__other`] || ""}
                  onOtherChange={(nextValue) => updateOther(field.id, nextValue)}
                  testId={`short-video-${field.id}-field`}
                />
              </div>
            ))}
          </div>

          <button type="button" onClick={generatePrompt} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A6753] px-6 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="short-video-generate-button">
            <Clapperboard className="h-4 w-4" aria-hidden="true" /> Luo lyhytvideoprompti
          </button>
        </section>

        <PromptResult
          title="Lyhytvideon valmis prompti"
          prompt={prompt}
          testIdPrefix="short-video-result"
          onSave={saveShortVideoPrompt}
        />
      </div>
    </div>
  );
}